/**
 * RSC — Retail Signing Center dashboard.
 *
 * Active signing sessions list filtered by status
 * (pending / partial / complete / voided / corrected).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { RefreshCw, Loader2, FileSignature } from 'lucide-react';
import {
  KpiTile,
  Pill,
  FilterChips,
  DataGrid,
  Button,
} from '@/components/primitives';
import type { DataGridColumn } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { formatLocal } from '@/utils/time';
import type { SigningSessionStatus } from '@retail-ops/shared-types/enums';
import './rsc.css';

type StatusFilter = 'all' | 'pending' | 'partial' | 'complete' | 'voided' | 'corrected';

interface SessionSummary {
  sessionId: string;
  cartId: string;
  status: SigningSessionStatus;
  signers: number;
  signed: number;
  createdAt: string;
  customerName?: string;
  groupId?: string;
}

// In-memory dev fixture — Wave 3 wires `apiClient.retail.sessions.list` or a
// dedicated `/v3/signing/sessions` route to source this list.
const DEV_SESSIONS: ReadonlyArray<SessionSummary> = import.meta.env.DEV
  ? [
      {
        sessionId: 'SESS-001',
        cartId: 'CART-9001',
        status: 'pending',
        signers: 2,
        signed: 0,
        createdAt: new Date(Date.now() - 2 * 3600_000).toISOString(),
        customerName: 'Smith, John',
        groupId: 'DG-001',
      },
      {
        sessionId: 'SESS-002',
        cartId: 'CART-9002',
        status: 'partial-signed',
        signers: 3,
        signed: 1,
        createdAt: new Date(Date.now() - 6 * 3600_000).toISOString(),
        customerName: 'Doe, Jane',
        groupId: 'DG-002',
      },
      {
        sessionId: 'SESS-003',
        cartId: 'CART-9003',
        status: 'completed',
        signers: 1,
        signed: 1,
        createdAt: new Date(Date.now() - 24 * 3600_000).toISOString(),
        customerName: 'Patel, Rani',
        groupId: 'DG-003',
      },
    ]
  : [];

function normalizedFilter(f: StatusFilter, s: SigningSessionStatus): boolean {
  if (f === 'all') return true;
  if (f === 'pending') return s === 'pending' || s === 'dispatched';
  if (f === 'partial') return s === 'partial-signed' || s === 'partial' || s === 'in_progress';
  if (f === 'complete') return s === 'completed' || s === 'complete' || s === 'all-signed';
  if (f === 'voided') return s === 'cancelled' || s === 'voided' || s === 'expired';
  if (f === 'corrected') return s === 'corrected' || s === 'bounce-recovery';
  return true;
}

export default function RSC(): JSX.Element {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [sessions, setSessions] = useState<ReadonlyArray<SessionSummary>>(DEV_SESSIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = async (): Promise<void> => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      // TODO (Wave 3): swap to real `/v3/signing/sessions` route when live.
      // For now we hit sessions.list to exercise the path, then fall back to fixture.
      await apiClient.retail.sessions.list({}, { signal: ctrl.signal });
      if (!ctrl.signal.aborted) setSessions(DEV_SESSIONS);
    } catch (err) {
      if (!ctrl.signal.aborted) {
        setError(err instanceof Error ? err.message : 'Failed to load sessions.');
      }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () => sessions.filter((s) => normalizedFilter(status, s.status)),
    [sessions, status],
  );

  const kpis = useMemo(() => {
    const pending = sessions.filter((s) => normalizedFilter('pending', s.status)).length;
    const partial = sessions.filter((s) => normalizedFilter('partial', s.status)).length;
    const complete = sessions.filter((s) => normalizedFilter('complete', s.status)).length;
    const voided = sessions.filter((s) => normalizedFilter('voided', s.status)).length;
    return { pending, partial, complete, voided };
  }, [sessions]);

  const columns: DataGridColumn<SessionSummary>[] = [
    {
      key: 'session',
      label: 'Session',
      render: (r) => (
        <span className="rsc-signer-row__body">
          <span className="rsc-signer-row__name">{r.sessionId}</span>
          <span className="rsc-signer-row__sub">Cart {r.cartId}</span>
        </span>
      ),
    },
    { key: 'customer', label: 'Customer', render: (r) => r.customerName ?? '—' },
    {
      key: 'signers',
      label: 'Signers',
      numeric: true,
      render: (r) => `${r.signed} / ${r.signers}`,
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => {
        const isComplete = r.status === 'completed' || r.status === 'all-signed';
        const isBad =
          r.status === 'cancelled' ||
          r.status === 'declined' ||
          r.status === 'expired' ||
          r.status === 'error';
        return (
          <Pill variant={isComplete ? 'ok' : isBad ? 'err' : 'info'}>{r.status}</Pill>
        );
      },
    },
    { key: 'created', label: 'Created', render: (r) => formatLocal(r.createdAt) },
  ];

  return (
    <div className="rsc-page">
      <header className="rsc-page__header">
        <div className="rsc-page__title-row">
          <h1>RSC — Signing Center</h1>
          <Pill variant="muted">Module RSC</Pill>
          <Pill variant="info">
            <FileSignature size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
            SignNow atomic `/signing/start`
          </Pill>
        </div>
        <p className="module-landing__scope">
          15 signing routes · 19 webhook subscriptions · 5 signer methods (email / QR-handoff /
          embedded / signature-pad / wet-sign). Post-submit corrections require Supervisor
          authority.
        </p>
      </header>

      <section className="rsc-kpi-grid">
        <KpiTile label="Pending" value={kpis.pending} />
        <KpiTile
          label="Partial"
          value={kpis.partial}
          severity={kpis.partial > 0 ? 'warn' : 'default'}
        />
        <KpiTile label="Complete (24h)" value={kpis.complete} />
        <KpiTile
          label="Voided / Expired"
          value={kpis.voided}
          severity={kpis.voided > 0 ? 'urgent' : 'default'}
        />
      </section>

      <div className="rsc-toolbar">
        <FilterChips<StatusFilter>
          label="Status"
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'partial', label: 'Partial' },
            { value: 'complete', label: 'Complete' },
            { value: 'voided', label: 'Voided' },
            { value: 'corrected', label: 'Corrected' },
            { value: 'all', label: 'All' },
          ]}
          selected={status}
          onChange={setStatus}
        />
        <Button
          variant="ghost"
          startIcon={loading ? <Loader2 size={14} /> : <RefreshCw size={14} />}
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <div className="rsc-banner rsc-banner--err" role="alert">
          {error}
        </div>
      )}

      <DataGrid<SessionSummary>
        columns={columns}
        rows={[...filtered]}
        rowKey={(r) => r.sessionId}
        onRowAction={(r) => navigate(`session/${r.sessionId}`)}
        emptyMessage="No signing sessions match the current filter."
      />
    </div>
  );
}
